"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReportEligibilityResponseSchema = exports.EligiblePeriodSchema = exports.EligibleScanSchema = exports.ReportPreferencesResponseSchema = exports.GenerateReportRequestSchema = exports.ReportPreferenceAnswersSchema = exports.MenstrualCycleAnswerSchema = exports.LifestyleContextAnswerSchema = exports.LifestyleAnswerSchema = exports.AdherenceAnswerSchema = exports.GetCalculatedContextRequestSchema = exports.CalculatedContextResponseSchema = exports.CalculatedLifestyleContextSchema = exports.AdherenceCalculatedDataSchema = exports.LifestyleCalculatedDataSchema = void 0;
const zod_1 = require("zod");
const constants_1 = require("../constants");
const enums_1 = require("./enums");
// ===== LIFESTYLE CALCULATED DATA =====
/**
 * Calculated data for a lifestyle question based on journal entries
 */
exports.LifestyleCalculatedDataSchema = zod_1.z.object({
    markedDays: zod_1.z.number().int().min(0),
    totalDays: zod_1.z.number().int().min(0),
    percentage: zod_1.z.number().min(0).max(100),
});
// ===== ADHERENCE CALCULATED DATA =====
/**
 * Calculated data for adherence based on routine completion tracking
 */
exports.AdherenceCalculatedDataSchema = zod_1.z.object({
    adherencePercentage: zod_1.z.number().min(0).max(100),
    totalScheduledPeriods: zod_1.z.number().int().min(0),
    completedPeriods: zod_1.z.number().int().min(0),
    hasTrackedData: zod_1.z.boolean(),
});
// ===== CALCULATED CONTEXT RESPONSE =====
exports.CalculatedLifestyleContextSchema = zod_1.z.object({
    sleep: exports.LifestyleCalculatedDataSchema.nullable(),
    stress: exports.LifestyleCalculatedDataSchema.nullable(),
    diet: exports.LifestyleCalculatedDataSchema.nullable(),
});
/**
 * Response containing all calculated context data for report generation wizard
 * Includes adherence and lifestyle (sleep, stress, diet) data
 */
exports.CalculatedContextResponseSchema = zod_1.z.object({
    adherence: exports.AdherenceCalculatedDataSchema.nullable(),
    lifestyle: exports.CalculatedLifestyleContextSchema,
    reportPeriod: zod_1.z
        .object({
        startDate: zod_1.z.date(),
        endDate: zod_1.z.date(),
        uniqueScanDays: zod_1.z.number().int().min(0),
    })
        .nullable(),
});
// ===== REQUEST SCHEMA =====
/**
 * Request to get calculated context data
 */
exports.GetCalculatedContextRequestSchema = zod_1.z.object({
    reportType: zod_1.z.enum(constants_1.skinReportTypeEnum),
});
// ===== WIZARD ANSWER TYPES =====
/**
 * Adherence answer from wizard - calculated or manual selection
 * Uses discriminated union for type-safe handling
 */
exports.AdherenceAnswerSchema = zod_1.z.discriminatedUnion('type', [
    zod_1.z.object({
        type: zod_1.z.literal('calculated'),
        percentage: zod_1.z.number().min(0).max(100),
        calculatedData: exports.AdherenceCalculatedDataSchema,
    }),
    zod_1.z.object({
        type: zod_1.z.literal('manual'),
        selection: zod_1.z.enum(enums_1.adherenceSelectionEnum),
    }),
]);
/**
 * Lifestyle answer for sleep, stress, or diet
 * Uses enums from ./enums for quality, source, and question type
 */
exports.LifestyleAnswerSchema = zod_1.z.object({
    question: zod_1.z.enum(enums_1.lifestyleQuestionEnum),
    quality: zod_1.z.enum(enums_1.lifestyleQualityEnum),
    source: zod_1.z.enum(enums_1.lifestyleAnswerSourceEnum),
    calculatedData: exports.LifestyleCalculatedDataSchema.optional(),
});
exports.LifestyleContextAnswerSchema = zod_1.z.object({
    sleep: exports.LifestyleAnswerSchema.optional(),
    stress: exports.LifestyleAnswerSchema.optional(),
    diet: exports.LifestyleAnswerSchema.optional(),
});
/**
 * Menstrual cycle answer - tracked with dates or not applicable
 * Uses discriminated union for type-safe handling
 */
exports.MenstrualCycleAnswerSchema = zod_1.z.discriminatedUnion('type', [
    zod_1.z.object({
        type: zod_1.z.literal('tracked'),
        lastPeriodStartDate: zod_1.z.string(), // ISO date string YYYY-MM-DD
        cycleLengthCategory: zod_1.z.enum(enums_1.cycleLengthCategoryEnum),
    }),
    zod_1.z.object({
        type: zod_1.z.literal('not_applicable'),
    }),
]);
/**
 * Complete wizard answers collected during report generation flow
 * Includes adherence, lifestyle (sleep/stress/diet), and menstrual cycle
 */
exports.ReportPreferenceAnswersSchema = zod_1.z.object({
    adherence: exports.AdherenceAnswerSchema,
    lifestyle: exports.LifestyleContextAnswerSchema,
    menstrualCycle: exports.MenstrualCycleAnswerSchema.optional(),
});
// ===== GENERATE REPORT REQUEST/RESPONSE =====
/**
 * Request to generate a skin report with wizard answers
 * Note: reportType is determined by the backend based on user's report history
 */
exports.GenerateReportRequestSchema = zod_1.z.object({
    reportPreferences: exports.ReportPreferenceAnswersSchema,
});
// ===== REPORT PREFERENCES =====
/**
 * Response containing saved report preferences for pre-population
 * Returns null if user has no saved preferences
 */
exports.ReportPreferencesResponseSchema = exports.ReportPreferenceAnswersSchema.nullable();
// ===== REPORT ELIGIBILITY =====
/**
 * Individual scan info for the eligibility period
 * Used to display scan thumbnails in the journey tile
 */
exports.EligibleScanSchema = zod_1.z.object({
    scanId: zod_1.z.string(),
    scanAt: zod_1.z.date(),
    thumbnailUrl: zod_1.z.string().url().optional(), // Signed URL or CDN URL for scan image
});
/**
 * Eligible period info when user can generate a report
 */
exports.EligiblePeriodSchema = zod_1.z.object({
    startDate: zod_1.z.date(),
    endDate: zod_1.z.date(),
    scanCount: zod_1.z.number().int().min(1),
});
/**
 * Response for report eligibility check
 *
 * Returns whether user can generate a report and progress info if not eligible.
 * - When eligible: includes eligiblePeriod with date range and scan count
 * - When not eligible: includes scansNeeded and daysRemaining (subsequent only)
 * - Always includes scans array (0-3 items) for journey tile visualization
 */
exports.ReportEligibilityResponseSchema = zod_1.z.object({
    isEligible: zod_1.z.boolean(),
    reportType: zod_1.z.enum(constants_1.skinReportTypeEnum),
    // When NOT eligible - progress info
    scansNeeded: zod_1.z.number().int().min(0).optional(),
    daysRemaining: zod_1.z.number().int().min(0).optional(),
    // When eligible - period info for CTA context
    eligiblePeriod: exports.EligiblePeriodSchema.optional(),
    // Scans in the current eligibility period (0-3 items)
    // Used to display thumbnails in the journey tile
    scans: zod_1.z.array(exports.EligibleScanSchema).optional(),
});
//# sourceMappingURL=report_lifecycle.js.map